'use client';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';

import { useQuery } from '@tanstack/react-query';
import { Dumbbell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  formatInTenantTz,
  resolveTimezone,
  tenantCalendarDayKey,
} from '@kit/shared/timezone';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  ClassStatusBadge,
  HorizontalCardScroller,
  SectionHeader,
} from '~/(tenant)/_components/native/_primitives';

interface TodayClassesSectionProps {
  timeZone?: string;
  accountSlug: string;
  /** UUID of the active venue from the location-switcher cookie. When set,
   *  the RPC scopes to sessions whose court belongs to this location. */
  locationId?: string | null;
}

interface ClassSession {
  booking_id: string;
  class_name: string;
  image_url: string | null;
  trainer_name: string | null;
  start_time: string;
  end_time: string;
  booking_date: string;
  spots_remaining: number;
  is_full: boolean;
  waitlist_enabled: boolean;
  show_spots_remaining: boolean;
  show_spots_remaining_last_only: boolean;
}

export function TodayClassesSection({
  accountSlug,
  locationId,
  timeZone,
}: TodayClassesSectionProps) {
  const t = useNativeTokens();
  const client = useSupabase();
  const tenantTimeZone = resolveTimezone(timeZone);

  const { data: classes, isLoading } = useQuery({
    queryKey: [
      'native-home-classes',
      accountSlug,
      tenantTimeZone,
      locationId ?? 'all',
    ],
    queryFn: async () => {
      const now = new Date();
      const today = tenantCalendarDayKey(now, tenantTimeZone);
      const endDate = tenantCalendarDayKey(
        now.getTime() + 7 * 86400000,
        tenantTimeZone,
      );
      const { data, error } = await client.rpc(
        'get_available_classes_for_date',
        {
          p_account_slug: accountSlug,
          p_date: today,
          p_end_date: endDate,
          p_location_id: locationId ?? undefined,
        },
      );
      if (error) throw error;
      const currentTime = formatInTenantTz(now, 'HH:mm:ss', tenantTimeZone);
      return ((data ?? []) as ClassSession[])
        .filter(
          (cls) =>
            cls.booking_date > today ||
            (cls.booking_date === today && cls.start_time > currentTime),
        )
        .slice(0, 10);
    },
    staleTime: 60_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  if (isLoading) {
    return (
      <section style={{ paddingTop: 32 }}>
        <SectionHeader
          eyebrow={
            <Trans i18nKey="common:nativeHome.thisWeek" defaults="This week" />
          }
          title={
            <Trans
              i18nKey="common:nativeHome.upcomingClasses"
              defaults="Upcoming classes"
            />
          }
        />
        <div
          style={{
            display: 'flex',
            gap: 14,
            paddingLeft: 22,
            paddingRight: 22,
            marginTop: 16,
            overflow: 'hidden',
          }}
        >
          {[0, 1].map((i) => (
            <div
              key={i}
              style={{
                height: 260,
                width: 220,
                flexShrink: 0,
                borderRadius: t.radius,
                backgroundColor: t.line,
              }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (!classes || classes.length === 0) return null;

  return (
    <section style={{ paddingTop: 32 }}>
      <SectionHeader
        eyebrow={
          <Trans i18nKey="common:nativeHome.thisWeek" defaults="This week" />
        }
        title={
          <Trans
            i18nKey="common:nativeHome.upcomingClasses"
            defaults="Upcoming classes"
          />
        }
        action={{
          label: <Trans i18nKey="common:seeAll" defaults="All" />,
          href: '/book?tab=classes',
        }}
      />

      <HorizontalCardScroller>
        {classes.map((cls) => (
          <ClassCard key={cls.booking_id} cls={cls} timeZone={tenantTimeZone} />
        ))}
      </HorizontalCardScroller>
    </section>
  );
}

function ClassCard({ cls, timeZone }: { cls: ClassSession; timeZone: string }) {
  const t = useNativeTokens();
  const { t: translate, i18n } = useTranslation();
  const bookingDate = new Date(cls.booking_date + 'T00:00:00');
  const now = new Date();
  const isToday = cls.booking_date === tenantCalendarDayKey(now, timeZone);
  const isTomorrow =
    cls.booking_date ===
    tenantCalendarDayKey(now.getTime() + 86400000, timeZone);

  const dateLabel = isToday
    ? translate('common:nativeHome.dateLabel.today')
    : isTomorrow
      ? translate('common:nativeHome.dateLabel.tomorrow')
      : formatLocaleDate(bookingDate, i18n.language, {
          weekday: 'short',
          day: undefined,
          month: undefined,
          year: undefined,
        });

  return (
    <Link
      href={`/event/${cls.booking_id}`}
      style={{
        flexShrink: 0,
        width: 220,
        textDecoration: 'none',
        color: t.foreground,
        scrollSnapAlign: 'start',
      }}
    >
      <div
        style={{
          position: 'relative',
          height: 260,
          borderRadius: t.radius,
          overflow: 'hidden',
          marginBottom: 12,
          backgroundColor: t.line,
        }}
      >
        {cls.image_url ? (
          <Image
            src={cls.image_url}
            alt={cls.class_name}
            fill
            className="object-cover"
            unoptimized
          />
        ) : (
          <div
            style={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              color: t.muted,
            }}
          >
            <Dumbbell size={36} strokeWidth={1.2} />
          </div>
        )}

        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.6))',
          }}
        />

        <ClassStatusBadge
          spotsRemaining={cls.spots_remaining}
          isFull={cls.is_full}
          waitlistEnabled={cls.waitlist_enabled}
          showSpotsRemaining={cls.show_spots_remaining}
          lastSpotOnly={cls.show_spots_remaining_last_only}
        />

        <div
          style={{
            position: 'absolute',
            bottom: 14,
            left: 14,
            right: 14,
            color: '#fff',
          }}
        >
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 8,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              opacity: 0.85,
              marginBottom: 4,
            }}
          >
            {dateLabel} · {formatTimeToAmPm(cls.start_time, i18n.language)}
          </div>
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 20,
              fontWeight: 400,
              lineHeight: 1.15,
            }}
          >
            {cls.class_name}
          </div>
          {cls.trainer_name && (
            <div
              style={{
                fontFamily: t.fontSans,
                fontSize: 11,
                color: 'rgba(255,255,255,0.85)',
                marginTop: 4,
              }}
            >
              {translate('common:nativeHome.withTrainer', {
                name: cls.trainer_name,
              })}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
